import React, { useState } from 'react';
import axios from 'axios';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BACKEND_URL } from '@/config';
import {
  Search, Shield, CheckCircle2, XCircle, Award, Clock, GraduationCap,
  Building2, Briefcase, Loader2, Copy, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${BACKEND_URL}/api`;

const EmployerVerifyPage = () => {
  const [certNumber, setCertNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleVerify = async (e) => {
    e?.preventDefault();
    const num = certNumber.trim().toUpperCase();
    if (!num) { toast.error('Enter a certificate number'); return; }

    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${API}/certificates/verify/employer/${num}`);
      setResult(res.data);
    } catch (err) {
      setResult(null);
      toast.error('Verification failed. Please try again.');
    } finally { setLoading(false); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/verify/employer?cert=${certNumber.trim().toUpperCase()}`;
    navigator.clipboard.writeText(url);
    toast.success('Verification link copied');
  };

  // Auto-verify from URL params
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cert = params.get('cert');
    if (cert) {
      setCertNumber(cert);
      setTimeout(() => {
        axios.get(`${API}/certificates/verify/employer/${cert}`)
          .then(res => { setResult(res.data); setSearched(true); })
          .catch(() => { setSearched(true); });
      }, 300);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="employer-verify-page">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#2a3e5d] to-[#1a2d4a] py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-[#02d1d4] text-xs font-semibold mb-4">
            <Shield className="h-3.5 w-3.5" /> Employer Verification Portal
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Verify a Certificate
          </h1>
          <p className="text-white/60 text-base max-w-xl mx-auto mb-8">
            Instantly verify the authenticity of certificates issued by Common Impact E-Learning.
            Enter the certificate number found on the learner's certificate.
          </p>

          <form onSubmit={handleVerify} className="max-w-lg mx-auto">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Enter certificate number (e.g., CI-A1B2C3D4)"
                value={certNumber}
                onChange={(e) => setCertNumber(e.target.value)}
                className="pl-14 pr-28 h-14 text-base font-medium bg-white text-[#2a3e5d] border-0 rounded-2xl shadow-xl placeholder:text-slate-400"
                data-testid="cert-number-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 px-6 bg-[#02d1d4] hover:bg-[#00b8bb] rounded-xl font-semibold"
                data-testid="verify-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="flex-1 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {loading && (
            <div className="text-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-[#20acf6] mx-auto mb-3" />
              <p className="text-slate-500">Verifying certificate...</p>
            </div>
          )}

          {!loading && searched && !result && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-8 text-center">
                <XCircle className="h-14 w-14 text-red-400 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-red-700">Certificate Not Found</h2>
                <p className="text-sm text-red-600 mt-2">
                  No certificate was found with the number provided. Please check the number and try again.
                </p>
              </CardContent>
            </Card>
          )}

          {!loading && result && (
            <div className="space-y-6" data-testid="verification-result">
              {/* Status Header */}
              <Card className={`border-2 ${result.verified ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 ${result.verified ? 'bg-emerald-100' : 'bg-red-100'}`}>
                      {result.verified ? (
                        <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                      ) : (
                        <XCircle className="h-8 w-8 text-red-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className={`text-xl font-bold ${result.verified ? 'text-emerald-800' : 'text-red-800'}`}>
                          {result.verified ? 'Certificate Verified' : 'Certificate Invalid'}
                        </h2>
                        {result.verified && result.is_job_ready && (
                          <Badge className="bg-[#20acf6] text-white text-xs gap-1" data-testid="job-ready-badge">
                            <Briefcase className="h-3 w-3" /> Job-Ready
                          </Badge>
                        )}
                      </div>
                      <p className={`text-sm mt-1 ${result.verified ? 'text-emerald-600' : 'text-red-600'}`}>
                        {result.message}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Certificate Details - only show when we have real data */}
              {result.learner_name && (
              <Card className="border-slate-200">
                <CardContent className="p-6 space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <DetailField icon={Search} label="Certificate Number" value={result.certificate_number} />
                    <DetailField icon={GraduationCap} label="Learner Name" value={result.learner_name} />
                    <DetailField icon={Award} label="Course Completed" value={result.course_title} />
                    <DetailField icon={Building2} label="Category" value={result.category || 'General'} />
                    <DetailField icon={Clock} label="Completion Date"
                      value={result.completion_date ? new Date(result.completion_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'} />
                    <DetailField icon={Clock} label="CPD Hours" value={result.cpd_hours ? `${result.cpd_hours} hours` : '-'} />
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                    {result.is_accredited && (
                      <Badge className="bg-purple-50 text-purple-700 border border-purple-200 text-xs gap-1">
                        <Award className="h-3 w-3" /> CPD Accredited
                      </Badge>
                    )}
                    {result.is_job_ready && (
                      <Badge className="bg-[#20acf6]/10 text-[#20acf6] border border-[#20acf6]/20 text-xs gap-1">
                        <Briefcase className="h-3 w-3" /> Job-Ready Certified
                      </Badge>
                    )}
                    {result.organisation_name && (
                      <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-xs gap-1">
                        <Building2 className="h-3 w-3" /> {result.organisation_name}
                      </Badge>
                    )}
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs gap-1">
                      <Shield className="h-3 w-3" /> Verified by Common Impact
                    </Badge>
                  </div>

                  {!result.verified && result.revoke_reason && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-700">Revocation Reason</p>
                        <p className="text-xs text-red-600">{result.revoke_reason}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Share - only when cert exists */}
              {result.learner_name && (
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" size="sm" onClick={copyLink} className="gap-1.5" data-testid="copy-verify-link">
                  <Copy className="h-3.5 w-3.5" /> Copy Verification Link
                </Button>
              </div>
              )}
            </div>
          )}

          {/* Info Section */}
          {!searched && (
            <div className="grid sm:grid-cols-3 gap-6 mt-8">
              {[
                { icon: Shield, title: 'Instant Verification', desc: 'Verify any Common Impact certificate in seconds using the certificate number.' },
                { icon: Briefcase, title: 'Job-Ready Badges', desc: 'Certificates for employability courses are marked with a Job-Ready badge.' },
                { icon: ExternalLink, title: 'API Access', desc: 'HR systems can query our API at /api/certificates/verify/employer/{id} for automated checks.' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <Card key={i} className="border-slate-200">
                    <CardContent className="p-5 text-center">
                      <div className="w-10 h-10 bg-[#02d1d4]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <Icon className="h-5 w-5 text-[#02d1d4]" />
                      </div>
                      <h3 className="text-sm font-semibold text-[#2a3e5d]">{item.title}</h3>
                      <p className="text-xs text-slate-500 mt-1">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const DetailField = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
      <Icon className="h-4 w-4 text-slate-500" />
    </div>
    <div>
      <p className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-[#2a3e5d] mt-0.5">{value || '-'}</p>
    </div>
  </div>
);

export default EmployerVerifyPage;
